import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { TranslateModule } from '@ngx-translate/core'
import { NgScrollbar, NgScrollbarModule } from 'ngx-scrollbar'
import { Subject, debounceTime, takeUntil } from 'rxjs'
import { TreeViewComponent } from './components/tree-view/tree-view.component'
import { TreeSectionService } from './tree-section.service'
import { fadeInOut, listAnimation } from '../../../../core/animations'
import { LocationGroupsService } from '../../../../core/api/locations-groups/locations-groups.service'
import { FcSearchInputAnimatedComponent } from '../../../../core/components/fc-search-input-animated/fc-search-input-animated.component'
import { FcSearchSelectComponent } from '../../../../core/components/fc-search-select/fc-search-select.component'
import { PulseAnimationComponent } from '../../../../core/components/pulse-animation/pulse-animation.component'
import { DirectivesModule } from '../../../../core/directives/directives.module'
import { AssetsTreeFilterService } from '../../services/assets-tree-filter.service'
import { AssetsTreeService } from '../../services/assets-tree.service'

@Component({
  selector: 'app-tree-section',
  templateUrl: './tree-section.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    fadeInOut,
    listAnimation
  ],
  imports: [
    CommonModule,
    MatIconModule,
    TranslateModule,
    PulseAnimationComponent,
    DirectivesModule,
    NgScrollbarModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    TreeViewComponent,
    FcSearchSelectComponent,
    FcSearchInputAnimatedComponent,
  ]
})
export class TreeSectionComponent implements OnInit, OnDestroy {
  public showLocationGroupInput = signal(false)
  public assetsTreeService = inject(AssetsTreeService)
  public treeSectionService = inject(TreeSectionService)
  public locationsGroupsService = inject(LocationGroupsService)
  public assetsTreeFilterService = inject(AssetsTreeFilterService)

  public readonly destroy$ = new Subject<void>()

  public ngOnInit(): void {
    this.assetsTreeFilterService.clearForm()

    this.assetsTreeFilterService.form.get('locationGroup')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.showLocationGroupInput.set(false)
      })

    this.assetsTreeFilterService.form.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(async () => {
        await this.assetsTreeFilterService.search()
      })
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  public trackByFn(index: number): number {
    return index
  }

  public onScrollbarUpdate(scrollbar: NgScrollbar) {
    scrollbar.scrolled.subscribe(async (event: any) => {
      await this.onScroll(event)
    })
  }

  public async onScroll(event: any): Promise<void> {
    const hasAllItems = this.assetsTreeFilterService.totalCount() === this.assetsTreeFilterService.locations().length
    if (hasAllItems || this.assetsTreeFilterService.isLoading() || this.assetsTreeFilterService.isLoadingMore()) {
      return
    }

    const scrollPosition: number = event.target.scrollTop
    const scrollHeight: number = event.target.scrollHeight
    const clientHeight: number = event.target.clientHeight
    const isNearBottom: boolean = scrollPosition + clientHeight >= scrollHeight
    if (isNearBottom) {
      await this.assetsTreeFilterService.search(true)
    }
  }
}
